import { useState } from 'react';
import type { ChartPoint, ChartSeries, TableColumn } from '../../ui';
import { Button, cx, EmptyState, Table, TableCell, TableRow, TableSection } from '../../ui';
import { formatCount } from './reportsModel';

/**
 * THE SAME NUMBERS, AS A TABLE (`spec/screens/reports.md` section 2 item 4, rulebook CH6:
 * "Every chart offers 'View as table'").
 *
 * It reads the CHART's data rather than the run result, so a bucket the fill invented
 * appears here too: the table and the chart are the same series, or they are two answers
 * to one question.
 *
 * IT PAGES BY FIFTY, which is section 4's own full state ("a table view of 1,000 rows
 * paging by 50"). A thousand rows in one DOM is a scroll region a phone cannot hold and a
 * measure sweep cannot read; fifty is the page, and the footer says where the reader is in
 * numerals with a real plural (copy deck R6).
 *
 * NO FRAME AROUND IT (P4-1). The head is Title Case in the secondary label over a
 * hairline, which is how Finder, Mail and Notes set one, and the rows carry hairlines;
 * the bordered, rounded box this table used to sit in is the card the split view removes.
 *
 * IT IS THE LIBRARY'S TABLE (`ui/Table`, BR-PARTS; DESIGN.md B1, C7, C10): columns as wide as
 * their content, the category column pinned while a wide report pans in its frame, and the
 * head under the toolbar while the rows scroll. Its cells keep this view's 8 by 12 px padding.
 */
export const TABLE_PAGE = 50;

/** This view's cell padding, `--space-2` down and `--space-3` across, as before the move. */
const CELL = 'px-3 py-2';

/**
 * A head's words as this view set them: the body size at the medium weight, over the part's
 * hairline in the secondary ink (P4-1).
 */
function headWords(words: string) {
  return <span className="text-sm font-medium">{words}</span>;
}

export interface ReportTableViewProps {
  readonly series: readonly ChartSeries[];
  readonly points: readonly ChartPoint[];
  readonly categoryHeading: string;
}

export function ReportTableView({ series, points, categoryHeading }: ReportTableViewProps) {
  const [shown, setShown] = useState(TABLE_PAGE);
  const columns = series.map((entry) => entry.label);
  const rows = points.map((point, index) => ({
    label: point.label,
    values: series.map((entry) => entry.points[index]?.value ?? null),
  }));

  if (rows.length === 0) {
    return <EmptyState icon="search" title="No data" description="Nothing in this range yet." />;
  }

  const page = rows.slice(0, shown);
  const remaining = rows.length - page.length;

  const tableColumns: readonly TableColumn[] = [
    { key: 'category', header: headWords(categoryHeading), headClassName: CELL },
    ...columns.map(
      (column, index): TableColumn => ({
        key: `series-${String(index)}`,
        header: headWords(column),
        align: 'end',
        headClassName: CELL,
      }),
    ),
  ];

  return (
    <div className="reports-table">
      <Table label={categoryHeading} columns={tableColumns}>
        <TableSection>
          {page.map((row, rowIndex) => (
            // Two categories CAN share a label - two options spelled the same, two buckets
            // a formatter rendered identically - so the row's position is its identity.
            <TableRow key={`${row.label}-${String(rowIndex)}`}>
              <TableCell column="category" rowHeader className={cx(CELL, 'text-ink')}>
                {row.label}
              </TableCell>
              {row.values.map((value, index) => (
                <TableCell
                  key={columns[index] ?? String(index)}
                  column={`series-${String(index)}`}
                  className={cx(CELL, 'tabular-nums')}
                >
                  {value === null ? 'No data' : formatCount(value)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableSection>
      </Table>
      <div className="reports-table-footer">
        <p className="text-2xs text-ink-muted tabular-nums">
          {`${formatCount(page.length)} of ${formatCount(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`}
        </p>
        {remaining > 0 && (
          <Button variant="ghost" onClick={() => setShown((current) => current + TABLE_PAGE)}>
            Show more
          </Button>
        )}
      </div>
    </div>
  );
}
